import { useCallback, useEffect, useRef, useState } from "react"

import type { Annotation } from "@/types/api/Annotation"

import {
  getReviewJson,
  type DecisionTreeReviewJson,
} from "./DecisionTreeReviewDetails"

interface UseDecisionTreeNodeToggleOptions {
  currentAnnotation: Annotation | null
  isReadOnly: boolean
  /** Resolves false when the save failed and the annotation was rolled back. */
  handleValueUpdate: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
    jsonValue?: object | null,
  ) => Promise<boolean>
}

/**
 * Toggles nodes in the reviewer's visited set. One save runs at a time:
 * toggles made while it runs update the shown set at once and are saved
 * together when it succeeds. A failed save drops those waiting toggles and
 * shows the annotation as rolled back, so no toggle is saved on top of a
 * failed one.
 */
export function useDecisionTreeNodeToggle({
  currentAnnotation,
  isReadOnly,
  handleValueUpdate,
}: UseDecisionTreeNodeToggleOptions) {
  const savedVisitedNodes = getReviewJson(currentAnnotation).visited_nodes
  const [unsavedVisitedNodes, setUnsavedVisitedNodes] = useState<
    string[] | null
  >(null)

  const unsavedRef = useRef<string[] | null>(null)
  const hasUnsentToggleRef = useRef(false)
  const isSavingRef = useRef(false)
  const isMountedRef = useRef(true)
  const annotationRef = useRef(currentAnnotation)
  annotationRef.current = currentAnnotation
  const handleValueUpdateRef = useRef(handleValueUpdate)
  handleValueUpdateRef.current = handleValueUpdate

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const setUnsaved = useCallback((visitedNodes: string[] | null) => {
    unsavedRef.current = visitedNodes
    setUnsavedVisitedNodes(visitedNodes)
  }, [])

  const saveUnsentToggles = useCallback(async () => {
    isSavingRef.current = true
    try {
      while (hasUnsentToggleRef.current && unsavedRef.current) {
        hasUnsentToggleRef.current = false
        const { score } = getReviewJson(annotationRef.current)
        const updatedJson: DecisionTreeReviewJson = {
          visited_nodes: unsavedRef.current,
          score,
        }
        const saved = await handleValueUpdateRef.current(
          score ?? null,
          null,
          null,
          updatedJson,
        )
        if (!isMountedRef.current) {
          return
        }
        if (!saved) {
          hasUnsentToggleRef.current = false
          break
        }
      }
      setUnsaved(null)
    } finally {
      isSavingRef.current = false
    }
  }, [setUnsaved])

  const toggleNode = useCallback(
    (nodeId: string) => {
      if (isReadOnly) {
        return
      }
      const visited = new Set(
        unsavedRef.current ??
          getReviewJson(annotationRef.current).visited_nodes,
      )
      if (visited.has(nodeId)) {
        visited.delete(nodeId)
      } else {
        visited.add(nodeId)
      }
      setUnsaved([...visited])
      hasUnsentToggleRef.current = true
      if (!isSavingRef.current) {
        void saveUnsentToggles()
      }
    },
    [isReadOnly, saveUnsentToggles, setUnsaved],
  )

  return {
    visitedNodes: unsavedVisitedNodes ?? savedVisitedNodes,
    toggleNode,
  }
}
